'use client';

import type { MediaPlayerRef } from '@govinity/ui/components/modules/media-player';
import type { MuxVideoPlayerRef } from '@govinity/ui/components/modules/mux-video-player';
import type {
  MeetingEventPublicAgendaSection,
  MeetingEventPublicGetResponse,
  MeetingEventTimestampPublicResponse,
} from '@govinity/validation/public';
import { createContext, useContext } from 'react';

export type MeetingContextValue = {
  meeting: MeetingEventPublicGetResponse;
  agendaSections: MeetingEventPublicAgendaSection[];
  sectionMap: Map<number, string>;
  itemMap: Map<number, string>;
  sectionNumberMap: Map<number, string>;
  itemNumberMap: Map<number, string>;
  /** Agenda-tree order indices keyed by section id / item placement id (for media-index ordering). */
  sectionOrderById: Map<number, number>;
  itemOrderById: Map<number, number>;
  timestamps: MeetingEventTimestampPublicResponse[];
  isLoadingTimestamps: boolean;
  timestampsError: Error | null;
  videoCurrentTime: number;
  handleVideoSeek: (time: number) => void;
  hasVideo: boolean;
  hasLiveVideo: boolean;
  showVideo: boolean;
  nowLabel: string | null;
  /**
   * Pixel offset from the top of the viewport at which the mobile sticky stack
   * ("Now:" + Show Layers) should pin. Pass the measured height of the
   * sticky tab strip above so they pin flush against it.
   */
  tabStripHeight: number;
  /** Seconds from a ?t= deep-link. Enables transcript layer, scrolls to and highlights the matched segment. */
  deepLinkTime: number | null;
  /** Optional search term from ?hl= to highlight within the matched transcript segment. */
  deepLinkHighlight: string | undefined;
  liveStreamData: { muxPlaybackId: string | null; isStreamActive: boolean } | undefined;
  recordingData:
    | { signedUrl: string | null; muxPlaybackId: string | null; thumbnailUrl: string | null }
    | undefined;
  floatingPlayerRef: React.RefObject<MuxVideoPlayerRef | MediaPlayerRef | null>;
};

export const MeetingContext = createContext<MeetingContextValue | null>(null);

export function useMeetingContext() {
  const context = useContext(MeetingContext);

  if (!context) {
    throw new Error('useMeetingContext must be used within a MeetingContext.Provider');
  }

  return context;
}
